import { createSlice } from '@reduxjs/toolkit';

import { PersonalDraftStepsEnum } from '../components/PersonalIntroduction/components/PersonalIntroductionContainer/enum';

import { getPersonalDraftData, getPersonalDraftStep, savePersonalDraftStep } from './personal.actions';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { DraftItemModel, RootPersonalDataStep } from '@shared/models/profileNew/profileNew.model';

export const createFormStepper = [
  PersonalDraftStepsEnum.personalPhotoAndHobbies,
  PersonalDraftStepsEnum.personalBodyParameters,
  PersonalDraftStepsEnum.personalHairColor,
  PersonalDraftStepsEnum.personalEyeColor,
  PersonalDraftStepsEnum.personalBodyArtTypes,
  PersonalDraftStepsEnum.insuranceBase,
];

type InitialState = {
  formStepper: any;
  currentStep: PersonalDraftStepsEnum;
  isCanChangeCurrentStep: boolean;
  draftItem: DraftItemModel | null;
};

const initialState: InitialState = {
  formStepper: {
    [PersonalDraftStepsEnum.initialStep]: {
      stepId: PersonalDraftStepsEnum.initialStep,
      isDisabled: false,
      isPassed: false,
      isSkip: false,
      id: 0,
    },
    [PersonalDraftStepsEnum.personalPhotoAndHobbies]: {
      stepId: PersonalDraftStepsEnum.personalPhotoAndHobbies,
      isDisabled: false,
      isPassed: false,
      isSkip: false,
      id: 1,
    },
    [PersonalDraftStepsEnum.personalBodyParameters]: {
      stepId: PersonalDraftStepsEnum.personalBodyParameters,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 2,
    },
    [PersonalDraftStepsEnum.personalHairColor]: {
      stepId: PersonalDraftStepsEnum.personalHairColor,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 3,
    },
    [PersonalDraftStepsEnum.personalEyeColor]: {
      stepId: PersonalDraftStepsEnum.personalEyeColor,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 4,
    },
    [PersonalDraftStepsEnum.personalBodyArtTypes]: {
      stepId: PersonalDraftStepsEnum.personalBodyArtTypes,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 5,
    },
    [PersonalDraftStepsEnum.insuranceBase]: {
      stepId: PersonalDraftStepsEnum.insuranceBase,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 6,
    },
    [PersonalDraftStepsEnum.share]: {
      stepId: PersonalDraftStepsEnum.share,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 7,
    },
    [PersonalDraftStepsEnum.congratulations]: {
      stepId: PersonalDraftStepsEnum.congratulations,
      isDisabledStep: false,
      isPassed: false,
      isSkip: false,
      id: 8,
    },
  },
  currentStep: PersonalDraftStepsEnum.initialStep,
  isCanChangeCurrentStep: true,
  draftItem: null,
};

export const personalDraftSlice = createSlice({
  name: 'personalDraft',
  initialState,
  reducers: {
    setPersonalDraftItem: (state, action: PayloadAction<any>) => {
      state.draftItem = action.payload;
    },
    setPersonalDraftCurrentStep: (state, action: PayloadAction<any>) => {
      state.currentStep = action.payload;
      state.isCanChangeCurrentStep = true;
    },
    setPersonalDraftCanChangeCurrentStep: (state, action: PayloadAction<boolean>) => {
      state.isCanChangeCurrentStep = action.payload;
    },
    setPersonalDraftFormStepPassed: (
      state,
      action: PayloadAction<{ step: PersonalDraftStepsEnum; isPassed?: boolean }>,
    ) => {
      const { step, isPassed = true } = action.payload;
      if (state.formStepper[step]) {
        state.formStepper[step].isPassed = isPassed;
      }
    },

    changePersonalFormStepperPassedDisableSteps: (
      state,
      action: PayloadAction<{
        enabledSteps: PersonalDraftStepsEnum[];
        passedStep: RootPersonalDataStep[];
      }>,
    ) => {
      const enabledSteps = new Set(action.payload.enabledSteps);

      action.payload.passedStep.forEach(({ step }) => {
        const currentStep = state.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });
    },

    resetPersonalDraftStore: () => {
      return initialState;
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getPersonalDraftData.fulfilled, (state, { payload }) => {
      state.draftItem = payload;
    });
    addCase(getPersonalDraftStep.fulfilled, (state, { payload }) => {
      if (payload.length) {
        const enabledSteps = new Set([PersonalDraftStepsEnum.initialStep]);

        payload.forEach(({ step }) => {
          const currentStep = state.formStepper[step];
          if (currentStep) {
            currentStep.isDisabled = enabledSteps.has(step);
            currentStep.isPassed = true;
          }
        });

        const lastStepInPayload = payload[payload.length - 1];
        if (lastStepInPayload) {
          state.currentStep =
            createFormStepper[createFormStepper.findIndex((item) => item === lastStepInPayload.step) + 1];
        }
      }
    });
    addCase(savePersonalDraftStep.fulfilled, (state, { payload }) => {
      state.currentStep = createFormStepper[createFormStepper.findIndex((item) => item === payload.step) + 1];
    });
  },
});

export const {
  setPersonalDraftItem,
  resetPersonalDraftStore,
  setPersonalDraftCurrentStep,
  setPersonalDraftCanChangeCurrentStep,
  setPersonalDraftFormStepPassed,
  changePersonalFormStepperPassedDisableSteps,
} = personalDraftSlice.actions;

export default personalDraftSlice.reducer;
